import { useCallback, useEffect, useState } from "react";
import type { AttendanceWithUser, SessionWithDetails } from "@/features/sessions/types";
import { getCheckedInCount, getUnresolvedCount } from "@/features/sessions/utils/attendanceCalculations";
import { getAttendanceStatus } from "@/features/sessions/utils/attendanceUtils";
import { getDefaultMode } from "@/features/sessions/utils/modeUtils";

type UseSessionDetailsPageReturn = {
    // State
    mode: "check-in" | "review";
    searchQuery: string;
    sortColumn: "name" | "table" | "status";
    sortDirection: "asc" | "desc";
    editingAttendanceId: string | null;

    // Actions
    setMode: (mode: "check-in" | "review") => void;
    setSearchQuery: (query: string) => void;
    handleSort: (column: "name" | "table" | "status") => void;
    setEditingAttendanceId: (id: string | null) => void;
    toggleCheckInTableFilter: (tableNumber: number) => void;

    // Computed values
    checkInTableFilter: number | null;
    checkedInCount: number;
    unresolvedCount: number;
    filteredAttendanceRecords: AttendanceWithUser[];
};

/**
 * useSessionDetailsPage hook encapsulates all business logic and state management
 * for the Session Details Page
 */
export function useSessionDetailsPage(
    session: SessionWithDetails | undefined,
    attendanceRecords: AttendanceWithUser[]
): UseSessionDetailsPageReturn {
    // Mode state - default based on session time
    const defaultMode = session ? getDefaultMode(session.scheduledAt) : "check-in";
    const [mode, setMode] = useState<"check-in" | "review">(defaultMode);

    // Search state
    const [searchQuery, setSearchQuery] = useState("");

    // Sort state (Review Mode only)
    const [sortColumn, setSortColumn] = useState<"name" | "table" | "status">("name");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

    // Edit state for Review Mode rows
    const [editingAttendanceId, setEditingAttendanceId] = useState<string | null>(null);

    // Check-in: filter list by table when a tally card is selected
    const [checkInTableFilter, setCheckInTableFilter] = useState<number | null>(null);

    // Track if user has manually changed mode
    const [userHasChangedMode, setUserHasChangedMode] = useState(false);

    // Update default mode when session changes (only if user hasn't interacted)
    useEffect(() => {
        if (session && !userHasChangedMode && searchQuery === "" && editingAttendanceId === null) {
            const newDefaultMode = getDefaultMode(session.scheduledAt);
            if (newDefaultMode !== mode) {
                setMode(newDefaultMode);
            }
        }
    }, [session, mode, searchQuery, editingAttendanceId, userHasChangedMode]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: reset table filter when session id changes
    useEffect(() => {
        setCheckInTableFilter(null);
    }, [session?.id]);

    const toggleCheckInTableFilter = useCallback((tableNumber: number) => {
        setCheckInTableFilter((prev) => (prev === tableNumber ? null : tableNumber));
    }, []);

    // Wrapper for setMode that tracks user interaction
    const handleSetMode = (newMode: "check-in" | "review") => {
        setUserHasChangedMode(true);
        if (newMode === "review") {
            setCheckInTableFilter(null);
        }
        setMode(newMode);
    };

    // Calculations
    const checkedInCount = getCheckedInCount(attendanceRecords);
    const unresolvedCount = getUnresolvedCount(attendanceRecords);

    // Filtering logic
    let filteredRecords = [...attendanceRecords];

    // Filter cancelled bookings in Check-in mode
    if (mode === "check-in") {
        filteredRecords = filteredRecords.filter((record) => record.cancelledAt == null);
    }

    // Apply search filter
    if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        filteredRecords = filteredRecords.filter((record) => {
            const name = record.user?.displayName ?? "";
            return name.toLowerCase().includes(query);
        });
    }

    if (mode === "check-in" && checkInTableFilter != null) {
        filteredRecords = filteredRecords.filter((record) => record.tableNumber === checkInTableFilter);
    }

    // Sorting logic (Review Mode only)
    if (mode === "review") {
        filteredRecords.sort((a, b) => {
            let comparison = 0;

            switch (sortColumn) {
                case "name": {
                    const nameA = a.user?.displayName ?? "Unknown";
                    const nameB = b.user?.displayName ?? "Unknown";
                    comparison = nameA.localeCompare(nameB);
                    break;
                }
                case "table": {
                    const tableA = a.tableNumber ?? 0;
                    const tableB = b.tableNumber ?? 0;
                    comparison = tableA - tableB;
                    break;
                }
                case "status": {
                    const statusA = getAttendanceStatus(a);
                    const statusB = getAttendanceStatus(b);
                    comparison = statusA.localeCompare(statusB);
                    break;
                }
            }

            return sortDirection === "asc" ? comparison : -comparison;
        });
    } else {
        // Check-in Mode: alphabetical by name
        filteredRecords.sort((a, b) => {
            const nameA = a.user?.displayName ?? "Unknown";
            const nameB = b.user?.displayName ?? "Unknown";
            return nameA.localeCompare(nameB);
        });
    }

    // Sort handler
    const handleSort = (column: "name" | "table" | "status") => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    return {
        checkedInCount,
        checkInTableFilter,
        editingAttendanceId,
        filteredAttendanceRecords: filteredRecords,
        handleSort,
        mode,
        searchQuery,
        setEditingAttendanceId,
        setMode: handleSetMode,
        setSearchQuery,
        sortColumn,
        sortDirection,
        toggleCheckInTableFilter,
        unresolvedCount,
    };
}
